/**
 * AudioManager - Centralized sound system for game UI
 *
 * Features:
 * - Preloads common UI sounds (with synthetic fallbacks)
 * - Volume controls (master, sfx)
 * - Muted by default (user preference)
 * - LocalStorage persistence for settings
 * - Sound categories: UI, actions, celebrations
 * - Web Audio API synthesized sounds as fallbacks
 */

import soundGenerator from './soundGenerator';

class AudioManager {
  constructor() {
    this.sounds = new Map();
    this.loadedSounds = new Set(); // Track which sounds loaded successfully
    this.settings = {
      enabled: false, // Muted by default
      masterVolume: 0.7,
      sfxVolume: 0.8,
    };

    // Map sound keys to synthetic generator methods
    this.syntheticFallbacks = {
      'click': () => soundGenerator.click(this.getVolume(0.5)),
      'hover': () => soundGenerator.hover(this.getVolume(0.15)),
      'open': () => soundGenerator.open(this.getVolume(0.4)),
      'close': () => soundGenerator.close(this.getVolume(0.35)),
      'toggle': () => soundGenerator.toggle(this.getVolume(0.4)),
      'success': () => soundGenerator.success(this.getVolume(0.5)),
      'error': () => soundGenerator.error(this.getVolume(0.4)),
      'warning': () => soundGenerator.warning(this.getVolume(0.4)),
      'coin': () => soundGenerator.coin(this.getVolume(0.5)),
      'item-pickup': () => soundGenerator.itemPickup(this.getVolume(0.45)),
      'craft-start': () => soundGenerator.craftStart(this.getVolume(0.45)),
      'craft-complete': () => soundGenerator.craftComplete(this.getVolume(0.55)),
      'equip': () => soundGenerator.itemPickup(this.getVolume(0.4)),
      'unlock': () => soundGenerator.success(this.getVolume(0.5)),
      'levelup': () => soundGenerator.levelUp(this.getVolume(0.6)),
      'achievement': () => soundGenerator.achievement(this.getVolume(0.6)),
      'loot-common': () => soundGenerator.lootReveal('common', this.getVolume(0.5)),
      'loot-rare': () => soundGenerator.lootReveal('rare', this.getVolume(0.55)),
      'loot-epic': () => soundGenerator.lootReveal('epic', this.getVolume(0.6)),
    };

    // Sound definitions with default volumes (for when MP3 files are available)
    this.soundDefinitions = {
      // UI Sounds
      'click': { src: '/sounds/ui/click.mp3', volume: 0.5, category: 'ui' },
      'hover': { src: '/sounds/ui/hover.mp3', volume: 0.2, category: 'ui' },
      'open': { src: '/sounds/ui/open.mp3', volume: 0.4, category: 'ui' },
      'close': { src: '/sounds/ui/close.mp3', volume: 0.4, category: 'ui' },
      'toggle': { src: '/sounds/ui/toggle.mp3', volume: 0.4, category: 'ui' },

      // Action Sounds
      'success': { src: '/sounds/actions/success.mp3', volume: 0.6, category: 'actions' },
      'error': { src: '/sounds/actions/error.mp3', volume: 0.5, category: 'actions' },
      'warning': { src: '/sounds/actions/warning.mp3', volume: 0.5, category: 'actions' },
      'coin': { src: '/sounds/actions/coin.mp3', volume: 0.5, category: 'actions' },
      'item-pickup': { src: '/sounds/actions/item-pickup.mp3', volume: 0.5, category: 'actions' },
      'craft-start': { src: '/sounds/actions/craft-start.mp3', volume: 0.5, category: 'actions' },
      'craft-complete': { src: '/sounds/actions/craft-complete.mp3', volume: 0.6, category: 'actions' },
      'equip': { src: '/sounds/actions/equip.mp3', volume: 0.5, category: 'actions' },
      'unlock': { src: '/sounds/actions/unlock.mp3', volume: 0.6, category: 'actions' },

      // Celebration Sounds
      'levelup': { src: '/sounds/celebrations/levelup.mp3', volume: 0.7, category: 'celebrations' },
      'achievement': { src: '/sounds/celebrations/achievement.mp3', volume: 0.7, category: 'celebrations' },
      'loot-common': { src: '/sounds/celebrations/loot-common.mp3', volume: 0.5, category: 'celebrations' },
      'loot-rare': { src: '/sounds/celebrations/loot-rare.mp3', volume: 0.6, category: 'celebrations' },
      'loot-epic': { src: '/sounds/celebrations/loot-epic.mp3', volume: 0.7, category: 'celebrations' },
    };

    this.loadSettings();
    this.preloadSounds();
  }

  /**
   * Get volume adjusted for master and sfx settings
   */
  getVolume(baseVolume) {
    return baseVolume * this.settings.masterVolume * this.settings.sfxVolume;
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    try {
      const saved = localStorage.getItem('audioSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.settings = { ...this.settings, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load audio settings:', e);
    }
  }

  /**
   * Save settings to localStorage
   */
  saveSettings() {
    try {
      localStorage.setItem('audioSettings', JSON.stringify(this.settings));
    } catch (e) {
      console.warn('Failed to save audio settings:', e);
    }
  }

  /**
   * Preload all sound files
   */
  preloadSounds() {
    Object.entries(this.soundDefinitions).forEach(([key, def]) => {
      const audio = new Audio();
      audio.src = def.src;
      audio.preload = 'auto';
      audio.volume = this.calculateVolume(def.volume);

      // Track successful loads
      audio.oncanplaythrough = () => {
        this.loadedSounds.add(key);
      };

      // Handle load errors gracefully (will fallback to synthetic)
      audio.onerror = () => {
        // Silent - synthetic fallback will be used
      };

      this.sounds.set(key, { audio, definition: def });
    });
  }

  /**
   * Calculate final volume considering all volume levels
   */
  calculateVolume(baseVolume) {
    return baseVolume * this.settings.masterVolume * this.settings.sfxVolume;
  }

  /**
   * Play a sound by key (uses synthetic fallback if file not loaded)
   * @param {string} soundKey - The sound identifier
   * @param {object} options - Optional overrides { volume, playbackRate }
   */
  play(soundKey, options = {}) {
    if (!this.settings.enabled) return;

    // Try synthetic sound first (always available)
    const syntheticFallback = this.syntheticFallbacks[soundKey];

    // If audio file is loaded, use it; otherwise use synthetic
    if (this.loadedSounds.has(soundKey)) {
      const sound = this.sounds.get(soundKey);
      if (sound) {
        try {
          const audio = sound.audio.cloneNode();
          const volume = options.volume !== undefined
            ? this.calculateVolume(options.volume)
            : this.calculateVolume(sound.definition.volume);

          audio.volume = Math.min(1, Math.max(0, volume));

          if (options.playbackRate) {
            audio.playbackRate = options.playbackRate;
          }

          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              // If audio file fails, try synthetic
              if (syntheticFallback) syntheticFallback();
            });
          }
          return;
        } catch (e) {
          // Fall through to synthetic
        }
      }
    }

    // Use synthetic fallback
    if (syntheticFallback) {
      try {
        syntheticFallback();
      } catch (e) {
        // Silent fail - sound is not critical
      }
    }
  }

  /**
   * Play UI click sound
   */
  playClick() {
    this.play('click');
  }

  /**
   * Play hover sound (with rate limiting)
   */
  playHover() {
    // Rate limit hover sounds to prevent spam
    const now = Date.now();
    if (this._lastHover && now - this._lastHover < 50) return;
    this._lastHover = now;
    this.play('hover', { volume: 0.15 });
  }

  /**
   * Play success sound
   */
  playSuccess() {
    this.play('success');
  }

  /**
   * Play error sound
   */
  playError() {
    this.play('error');
  }

  /**
   * Play coin/currency sound
   */
  playCoin() {
    this.play('coin');
  }

  /**
   * Play level up celebration
   */
  playLevelUp() {
    this.play('levelup');
  }

  /**
   * Play achievement unlock sound
   */
  playAchievement() {
    this.play('achievement');
  }

  /**
   * Play loot reveal sound based on rarity
   * @param {string} rarity - common, rare, epic
   */
  playLootReveal(rarity = 'common') {
    const soundKey = `loot-${rarity}`;
    if (this.sounds.has(soundKey)) {
      this.play(soundKey);
    } else {
      this.play('loot-common');
    }
  }

  /**
   * Enable/disable sounds
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.settings.enabled = enabled;
    this.saveSettings();

    // Play a confirmation sound when enabling
    if (enabled) {
      setTimeout(() => this.play('toggle'), 50);
    }
  }

  /**
   * Toggle sound enabled state
   * @returns {boolean} New enabled state
   */
  toggle() {
    this.setEnabled(!this.settings.enabled);
    return this.settings.enabled;
  }

  /**
   * Check if sounds are enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.settings.enabled;
  }

  /**
   * Set master volume
   * @param {number} volume - 0 to 1
   */
  setMasterVolume(volume) {
    this.settings.masterVolume = Math.min(1, Math.max(0, volume));
    this.saveSettings();
    this.updateAllVolumes();
  }

  /**
   * Set SFX volume
   * @param {number} volume - 0 to 1
   */
  setSfxVolume(volume) {
    this.settings.sfxVolume = Math.min(1, Math.max(0, volume));
    this.saveSettings();
    this.updateAllVolumes();
  }

  /**
   * Update volumes for all loaded sounds
   */
  updateAllVolumes() {
    this.sounds.forEach((sound) => {
      sound.audio.volume = this.calculateVolume(sound.definition.volume);
    });
  }

  /**
   * Get current settings
   * @returns {object}
   */
  getSettings() {
    return { ...this.settings };
  }
}

// Export singleton instance
const audioManager = new AudioManager();
export default audioManager;

// Export class for testing
export { AudioManager };
